import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { Apollo } from 'apollo-angular'
import { firstValueFrom } from 'rxjs'
import { ListNotificationsDocument, ListNotificationsQueryVariables, Notification, NotificationReadAllDocument, NotificationReadDocument, NotificationReadMutationVariables, NotificationType } from '../../../../../../main/src/generated/mordu.graphql'
import { TranslatePendencyTreatmentStatusPipe } from '../../../pipes/pendency-treatment-status.pipe'
import { RemainingTimePipe } from '../../../pipes/remaining-time.pipe'
import { MappedNotification } from '../../interfaces/notification.interface'
import { TranslationLoaderService } from '../i18n/i18n.service'


@Injectable({
  providedIn: 'root',
})
export class ToolbarNotificationService {
  public constructor(
    private readonly apollo: Apollo,
    private readonly translateService: TranslateService,
    private readonly remainingTimePipe: RemainingTimePipe,
    private readonly translatePendencyTreatmentStatusPipe: TranslatePendencyTreatmentStatusPipe,
    private readonly translationLoaderService: TranslationLoaderService
  ) {
    this.translationLoaderService.initialize()
  }

  public async listNotifications(variables: ListNotificationsQueryVariables): Promise<MappedNotification[]> {
    const { data } = await firstValueFrom(this.apollo.use('mordu').query({
      query: ListNotificationsDocument,
      variables,
    })) as Record<string, any>

    return data.notifications.map((notification: Notification) => ({
      ...notification,
      ...this.getMappedNotificationCustomColumns(notification)
    }) as MappedNotification)
  }

  public async readNotification(variables: NotificationReadMutationVariables): Promise<void> {
    await firstValueFrom(this.apollo.use('mordu').mutate({
      mutation: NotificationReadDocument,
      variables,
    }))

    window.dispatchEvent(new CustomEvent('refresh-notification-count'))
  }

  public async readAllNotifications(): Promise<void> {
    await firstValueFrom(this.apollo.use('mordu').mutate({
      mutation: NotificationReadAllDocument,
    }))

    window.dispatchEvent(new CustomEvent('refresh-notification-count'))
  }

  public getMappedNotificationCustomColumns(notification: Notification): Partial<MappedNotification> {
    const values = { center: {}, toolbar: {} } as Partial<MappedNotification>

    if (notification.type === NotificationType.MaintenanceOnNegotiation) {
      values.toolbar = { class: 'bg-color-primary', icon: 'mail' }
      values.center = { class: 'text-color-warning', icon: 'mark_email_unread' }
      values.title = this.translateService.instant('NOTIFICATIONS.MAINTENANCE.ON_NEGOTIATION', {
        number: notification.data.maintenance.number
      })
    }

    if (notification.type === NotificationType.MaintenanceCommented) {
      values.toolbar = {
        class: notification.data.comment.type === 'company' ? 'bg-[#ff7518]' : 'bg-[#12b886]',
        icon: notification.data.comment.type === 'company' ? 'business' : 'group'
      }

      const truncatedMessage = notification.data.comment.message.length > 100
      ? `${notification.data.comment.message.slice(0, 100)}...`
      : notification.data.comment.message

      values.title = this.translateService.instant('NOTIFICATIONS.MAINTENANCE.COMMENTED', {
        name: notification.data.author.name.split(' ').shift(),
        message: truncatedMessage
      })
    }

    if (notification.type === NotificationType.MaintenanceCanceled) {
      values.toolbar = { class: 'bg-[#b099f7]', icon: 'event_busy' }
      values.center = { class: 'text-[#c15eb4]', icon: 'event_busy' }
      values.title = this.translateService.instant('NOTIFICATIONS.MAINTENANCE.CANCELED', {
        number: notification.data.maintenance.number
      })
    }

    if (notification.type === NotificationType.MaintenanceResponseDeadlineViolated) {
      values.toolbar = { class: 'bg-[#e84b6a]', icon: 'schedule' }
      values.center = { class: 'text-color-danger', icon: 'watch_later' }
      values.title = this.translateService.instant('NOTIFICATIONS.MAINTENANCE.RESPONSE_DEADLINE_VIOLATED')
    }

    if (notification.type === NotificationType.MaintenanceSolutionDeadlineViolated) {
      values.toolbar = { class: 'bg-[#e84b6a]', icon: 'schedule' }
      values.center = { class: 'text-color-danger', icon: 'watch_later' }
      values.title = this.translateService.instant('NOTIFICATIONS.MAINTENANCE.SOLUTION_DEADLINE_VIOLATED')
    }

    if (notification.type === NotificationType.MaintenanceResponseDeadlineAtRisk) {
      values.toolbar = { class: 'bg-[#d67d38]', icon: 'schedule' }
      values.center = { class: 'text-color-warning', icon: 'watch_later' }
      values.title = this.translateService.instant('NOTIFICATIONS.MAINTENANCE.RESPONSE_DEADLINE_AT_RISK', {
        remainingTime: this.remainingTimePipe.transform(
          notification.data.maintenance.firstVisitRiskAt,
          notification.data.maintenance.firstVisitDeadlineAt
        )
      })
    }

    if (notification.type === NotificationType.MaintenanceSolutionDeadlineAtRisk) {
      values.toolbar = { class: 'bg-[#d67d38]', icon: 'schedule' }
      values.center = { class: 'text-color-warning', icon: 'watch_later' }
      values.title = this.translateService.instant('NOTIFICATIONS.MAINTENANCE.SOLUTION_DEADLINE_AT_RISK', {
        remainingTime: this.remainingTimePipe.transform(
          notification.data.maintenance.deadlineRiskAt,
          notification.data.maintenance.deadlineAt
        )
      })
    }

    if (notification.type === NotificationType.TicketCanceled) {
      values.toolbar = { class: 'bg-color-danger', icon: 'close' }
      values.center = { class: 'text-color-danger', icon: 'cancel' }
      values.title = this.translateService.instant('NOTIFICATIONS.TICKET.CANCELED', {
        number: notification.data.maintenance.number
      })
    }

    if (notification.type === NotificationType.QuotationCreated) {
      values.toolbar = { class: 'bg-[#6633ff]', icon: 'attach_money' }
      values.center = { class: 'text-[#2fb688]', icon: 'attach_money' }
      values.title = this.translateService.instant('NOTIFICATIONS.QUOTATION.CREATED', {
        name: notification.data.author.name,
        identifier: notification.data.quotation.identifier
      })
    }

    if (notification.type === NotificationType.PendencyCreated) {
      values.toolbar = { class: 'bg-color-tertiary', icon: 'pendency-filled', isSvg: true }
      values.center = { class: 'text-[#002266]', icon: 'pendency-filled', isSvg: true }
      values.title = this.translateService.instant('NOTIFICATIONS.PENDENCY.CREATED')
    }

    if (notification.type === NotificationType.PendencyCompleted) {
      values.toolbar = { class: 'bg-[#2fb688]', icon: 'pendency-filled', isSvg: true }
      values.center = { class: 'text-[#2fb688]', icon: 'pendency-filled', isSvg: true }
      values.title = this.translateService.instant('NOTIFICATIONS.PENDENCY.COMPLETED', {
        identifier: notification.data.pendency.identifier
      })
    }

    if (notification.type === NotificationType.PendencyTransferred) {
      values.toolbar = { class: 'bg-[#e2772e]', icon: 'pendency-filled', isSvg: true }
      values.center = { class: 'text-[#e2772e]', icon: 'pendency-filled', isSvg: true }
      values.title = this.translateService.instant('NOTIFICATIONS.PENDENCY.TRANSFERRED', {
        identifier: notification.data.pendency.identifier,
        treatmentStatus: this.translatePendencyTreatmentStatusPipe.transform(
          notification.data.pendency.treatmentStatus
        )
      })
    }

    if (notification.type === NotificationType.PendencyReplied) {
      values.toolbar = { class: 'bg-[#439be6]', icon: 'pendency-filled', isSvg: true }
      values.center = { class: 'text-[#439be6]', icon: 'pendency-filled', isSvg: true }

      const hasMessage = Boolean(notification.data.pendencyEvent.message)
      values.title = this.translateService.instant(`NOTIFICATIONS.PENDENCY.${hasMessage ? 'REPLIED' : 'REPLIED_WITHOUT_TEXT'}`, {
        identifier: notification.data.pendency.identifier,
        message: notification.data.pendencyEvent.message
      })
    }

    if (notification.type === NotificationType.StorageEntry) {
      values.toolbar = { class: 'bg-[#F6F9FD]', textClass: 'text-color-emerald-40', icon: 'swap_vert' }
      values.center = { class: 'text-color-emerald-40', icon: 'swap_vert' }
      values.title = this.translateService.instant('NOTIFICATIONS.STORAGE.ENTRY', {
        product: notification.data.product.name,
        storage: notification.data.storage.name,
      })
    }

    if (notification.type === NotificationType.StorageRemoval) {
      values.toolbar = { class: 'bg-[#F6F9FD]', textClass: 'text-color-red-40', icon: 'swap_vert' }
      values.center = { class: 'text-color-red-40', icon: 'swap_vert' }
      values.title = this.translateService.instant('NOTIFICATIONS.STORAGE.REMOVAL', {
        product: notification.data.product.name,
        storage: notification.data.storage.name,
      })
    }

    if (notification.type === NotificationType.StorageInternalTransfer) {
      values.toolbar = { class: 'bg-[#F6F9FD]', textClass: 'text-color-new-gray-50', icon: 'swap_horiz' }
      values.center = { class: 'text-color-new-gray-50', icon: 'swap_horiz' }
      values.title = this.translateService.instant('NOTIFICATIONS.STORAGE.INTERNAL_TRANSFER', {
        product: notification.data.product.name
      })
    }

    if (notification.type === NotificationType.StorageExternalTransfer) {
      values.toolbar = { class: 'bg-[#F6F9FD]', textClass: 'text-color-new-gray-50', icon: 'swap_horiz' }
      values.center = { class: 'text-color-new-gray-50', icon: 'swap_horiz' }
      values.title = this.translateService.instant('NOTIFICATIONS.STORAGE.EXTERNAL_TRANSFER', {
        product: notification.data.product.name,
        company: notification.data.company.name
      })
    }

    if (notification.type === NotificationType.StorageMinQuantityAtRisk) {
      values.toolbar = { class: 'bg-[#F6F9FD]', textClass: 'text-color-new-gray-50', icon: 'storage-filled', isSvg: true }
      values.center = { class: 'text-color-new-gray-50', icon: 'storage-filled', isSvg: true }
      values.title = this.translateService.instant('NOTIFICATIONS.STORAGE.MIN_QUANTITY_AT_RISK', {
        product: notification.data.product.name,
        storage: notification.data.storage.name
      })
    }

    if (notification.type === NotificationType.StorageMinQuantity) {
      values.toolbar = { class: 'bg-[#F6F9FD]', textClass: 'text-color-yellow-50', icon: 'storage-filled', isSvg: true }
      values.center = { class: 'text-color-yellow-50', icon: 'storage-filled', isSvg: true }
      values.title = this.translateService.instant('NOTIFICATIONS.STORAGE.MIN_QUANTITY', {
        product: notification.data.product.name,
        storage: notification.data.storage.name
      })
    }

    if (notification.type === NotificationType.StorageMinQuantityViolated) {
      values.toolbar = { class: 'bg-[#F6F9FD]', textClass: 'text-color-orange-60', icon: 'storage-filled', isSvg: true }
      values.center = { class: 'text-color-orange-60', icon: 'storage-filled', isSvg: true }
      values.title = this.translateService.instant('NOTIFICATIONS.STORAGE.MIN_QUANTITY_VIOLATED', {
        product: notification.data.product.name,
        storage: notification.data.storage.name
      })
    }

    if (notification.type === NotificationType.StorageEmptyQuantity) {
      values.toolbar = { class: 'bg-[#F6F9FD]', textClass: 'text-color-red-40', icon: 'storage-filled', isSvg: true }
      values.center = { class: 'text-color-red-40', icon: 'storage-filled', isSvg: true }
      values.title = this.translateService.instant('NOTIFICATIONS.STORAGE.EMPTY_QUANTITY', {
        product: notification.data.product.name,
        storage: notification.data.storage.name
      })
    }

    if (notification.type === NotificationType.CompanyInviteAccepted) {
      values.toolbar = { class: 'bg-[#2fb688]', icon: 'check_circle' }
      values.center = { class: 'text-[#2fb688]', icon: 'check_circle' }
      values.title = `${notification.data.invite.email} aceitou o convite`
    }

    if (notification.type === NotificationType.CompanyInviteRefused) {
      values.toolbar = { class: 'bg-[#e84b6a]', icon: 'cancel' }
      values.center = { class: 'text-[#e84b6a]', icon: 'cancel' }
      values.title = `${notification.data.invite.email} recusou o convite`
    }

    return values
  }
}
